import DropdownField, { VARIABLE_TYPE } from "@/components/shared/formFields/dropdownField";
import VariableField from "@/components/shared/formFields/variableField";
import Button from "@/components/shared/ui/Button";
import { Loader } from "@/components/shared/ui/Loader";
import useGetAdditionalInfo from "@/hooks/methodology/useGetAdditionalInfo";
import usePostAdditionalInfo from "@/hooks/methodology/usePostAdditionalInfo";
import { MethodologyAdditionalInfoResponse } from "@/types/AdditionalInfo";
import { MethodologyWithRoundDetails } from "@/types/Methodology";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { z } from "zod";

const QuantificationParameterSchema = z.object({
	parameter: z.string().max(100).min(1),
	parameterDescription: z.string().max(100).min(1),
});
const QuantificationParametersArraySchema = z.array(QuantificationParameterSchema);

export type QuantificationParameter = {
	id: number;
	parameterDescription: string;
	parameter: string;
	type: string;
};

interface QuantificationTabProps {
	onSubmitSuccessFn: () => void;
	isOriginator?: boolean;
	roundStatus?: string;
	roundDetails?: MethodologyWithRoundDetails;
}

const Quantification = (props: QuantificationTabProps) => {
	const { onSubmitSuccessFn, roundDetails } = props;
	const router = useRouter();
	const { query } = router;
	const params = query;
	const [backupParameterList, setBackupParameterList] = useState<QuantificationParameter[]>([
		{
			id: 1,
			parameterDescription: "",
			parameter: "",
			type: VARIABLE_TYPE.MEASURED,
		},
	]);
	const [parameterList, setParameterList] = useState<QuantificationParameter[]>([
		{
			id: 1,
			parameterDescription: "",
			parameter: "",
			type: VARIABLE_TYPE.MEASURED,
		},
	]);
	const [errors, setErrors] = useState<boolean[]>([]);
	const { data, isLoading } = useGetAdditionalInfo(params.id?.toString());
	const { mutate: submitAdditionalInfo, isLoading: isSubmitting } = usePostAdditionalInfo(
		"GHG Emission Calculation Builder Parameters",
		onSubmitSuccessFn,
	);
	const canEditData =
		data?.status === "CONDITIONALLY_APPROVED" &&
		props.isOriginator &&
		!(data?.methodologyAdditionalInfo?.status === "SUBMITTED");

	useEffect(() => {
		if (!data) return;
		if (data.methodologyAdditionalInfo?.variableList) {
			populateInitialData(data);
		}
	}, [data]);

	const populateInitialData = (data: MethodologyAdditionalInfoResponse) => {
		setParameterList(
			data.methodologyAdditionalInfo.variableList.map((item, index) => {
				return {
					id: index,
					parameterDescription: item.variableName,
					parameter: item.variableAbbreviation,
					type: item.variableType,
				};
			}),
		);
		setBackupParameterList(
			data.methodologyAdditionalInfo.variableList.map((item, index) => {
				return {
					id: index,
					parameterDescription: item.variableName,
					parameter: item.variableAbbreviation,
					type: item.variableType,
				};
			}),
		);
	};

	const prepareFormData = (): FormData => {
		const formData: any = new FormData();
		formData.append("methodologyId", params.id?.toString()!);
		if (data?.methodologyAdditionalInfo?.id) {
			formData.append("methodologyAdditionalInfoId", data.methodologyAdditionalInfo.id);
		}

		parameterList.forEach((parameterItem, index) => {
			formData.append(`variableList[${index}][variableName]`, parameterItem.parameterDescription);
			formData.append(`variableList[${index}][variableAbbreviation]`, parameterItem.parameter);
			formData.append(`variableList[${index}][variableType]`, parameterItem.type);
		});
		return formData;
	};

	const handleAddField = () => {
		setParameterList([
			...parameterList,
			{
				id: Math.floor(Math.random() * 100 + 1),
				parameterDescription: "",
				parameter: "",
				type: VARIABLE_TYPE.MEASURED,
			},
		]);
	};

	const handleParameterChange = (value: string, index: number) => {
		const newParameterList = [...parameterList];
		newParameterList[index].parameter = value;
		setParameterList(newParameterList);
	};

	const handleParameterDescChange = (value: string, index: number) => {
		const newParameterList = [...parameterList];
		newParameterList[index].parameterDescription = value;
		setParameterList(newParameterList);
	};

	const handleParameterTypeChange = (value: string, index: number) => {
		const newParameterList = [...parameterList];
		newParameterList[index].type = value;
		setParameterList(newParameterList);
	};

	const handleDelete = (index: number) => {
		const newList = parameterList.filter((list) => list.id != index);
		setParameterList(newList);
	};

	const handleSubmit = () => {
		try {
			QuantificationParametersArraySchema.parse(parameterList);
		} catch (error) {
			let currentErrors: boolean[] = [];
			for (let idx in parameterList) {
				try {
					QuantificationParameterSchema.parse({
						parameter: parameterList[idx].parameter,
						parameterDescription: parameterList[idx].parameterDescription,
					});
				} catch (error) {
					currentErrors[idx] = true;
				}
			}
			setErrors(currentErrors);
			return;
		}
		const formData = prepareFormData();
		submitAdditionalInfo(formData);
		setBackupParameterList(
			parameterList.map((item) => {
				return { ...item };
			}),
		);
	};

	const handleCancel = () => {
		setParameterList(
			backupParameterList.map((item) => {
				return { ...item };
			}),
		);
	};

	if (isLoading) return <Loader />;

	return (
		<div className="space-y-12 px-4">
			{parameterList.length === 1 && !parameterList[0].parameterDescription && !canEditData ? (
				<></>
			) : (
				<span className="text-2xl font-bold">
					{roundDetails && roundDetails.status !== "APPROVED"
						? "GHG Emission Calculation Builder"
						: "GHG Emission Calculation"}
				</span>
			)}

			{roundDetails && roundDetails.status !== "APPROVED" && (
				<div className="w-full space-y-5 text-sm md:w-full lg:w-1/3">
					<p>Please enter all parameters used in Emission Calculation in this methodology below.</p>
					<p>
						Indicate whether each parameter is Monitored (data will be provided each monitoring period) or Set (data
						remains constant throughout project).
					</p>
				</div>
			)}

			{parameterList.length === 1 && !parameterList[0].parameterDescription && !canEditData ? (
				<span className="text-base font-medium">No GHG parameters available.</span>
			) : (
				parameterList.map((item, index) => (
					<div key={item.id.toString() + index.toString()}>
						<div className="grid items-center gap-4 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-6">
							<VariableField
								label="Parameter Description"
								value={parameterList[index].parameterDescription}
								onChange={(e: any) => handleParameterDescChange(e.target.value, index)}
								disabled={!canEditData}
							/>
							<div className="flex flex-col">
								<span className="mb-2 text-sm font-semibold">Parameter</span>
								<math-field
									style={{
										borderWidth: "2px",
										borderStyle: "solid",
										borderColor: "##D1D5DA",
									}}
									onInput={(e: any) => handleParameterChange(e.target.value, index)}
									value={parameterList[index].parameter}
									{...(!canEditData && { "read-only": true })}
								/>
							</div>

							<DropdownField
								value={parameterList[index].type}
								onChange={(e: any) => handleParameterTypeChange(e.target.value, index)}
								disabled={!canEditData}
							/>
							{canEditData && (
								<div className="flex w-full items-center justify-start">
									<Button
										onClick={() => handleDelete(item.id)}
										color="danger"
										type="button"
										className="mt-7 items-start text-center text-sm"
										variant={parameterList.length == 1 ? "ghost" : "solid"}
										disabled={parameterList.length == 1}
									>
										Delete
									</Button>
								</div>
							)}
						</div>
						{errors[index] && <p className="mt-1 text-red-500">Parameter and Parameter description are required</p>}
					</div>
				))
			)}

			{canEditData && (
				<div>
					<Button onClick={handleAddField} variant="outline" className="flex items-center">
						<span className="mr-2 text-2xl">+</span> Add Another
					</Button>
				</div>
			)}

			{canEditData && (
				<div className="flex gap-2 align-middle">
					<Button onClick={handleCancel} variant="outline">
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						variant={isSubmitting ? "ghost" : "solid"}
						className="text-black"
						disabled={isSubmitting}
					>
						Save changes
					</Button>
				</div>
			)}
		</div>
	);
};

export default Quantification;
